import { StowageInstructionResponseDto } from '@storage/app/api'
import { StowageInstructionStore } from '@storage/stores/stowage-instruction.store'

import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ConfirmationDialog,
  OptionsButton,
  TomTable,
  TomTableActionsProps,
  TomTableEmptyCell,
  tomTableColumnFilterPresets,
  useTomTable,
} from '@tom-ui/ui'
import { appStore, useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useStowageInstructionMessageHandler } from '../../hooks/useStowageInstructionMessageHandler'

interface Props {
  store: StowageInstructionStore
}

export const StowageInstructionTable = observer(({ store }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { getExceptionMessage, getSuccessMessage } = useStowageInstructionMessageHandler()
  const [deleteItems, setDeleteItems] = useState<{ id: string; name: string }[]>([])

  const handleDelete = async () => {
    if (!deleteItems.length) return
    try {
      await store.deleteStowageInstructions(deleteItems.map(item => item.id))
      appStore.setShowAlert('success', getSuccessMessage())
      setDeleteItems([])
    } catch (error) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const columns = useMemo<MRT_ColumnDef<StowageInstructionResponseDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'description',
        header: t('description', 'Description'),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const tableActions = useMemo<TomTableActionsProps<StowageInstructionResponseDto>[]>(
    () => [
      {
        label: t('delete', 'Delete'),
        onClick: rows => {
          const itemsToDelete = rows.map(row => ({
            id: row.original.id,
            name: row.original.name,
          }))
          setDeleteItems(itemsToDelete)
        },
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'storage/stowage-instructions',
  )

  const table = useTomTable<StowageInstructionResponseDto>(
    {
      columns,
      data: store.entries,
      enableExpanding: false,
      layoutMode: 'grid',
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () => navigate(`${row.original.id}`),
            testId: `edit-${row.original.id}`,
          },
          {
            label: t('delete', 'Delete'),
            onClick: () => setDeleteItems([{ id: row.original.id, name: row.original.name }]),
            testId: `delete-${row.original.id}`,
            color: 'error',
          },
        ]

        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='stowage-instruction-options-btn'
          />
        )
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 202px)',
          minHeight: 'calc(100vh - 202px)',
        },
      },
    },
    tableActions,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <>
      <TomTable table={table} />
      <ConfirmationDialog
        open={deleteItems.length > 0}
        title={
          deleteItems.length === 1
            ? t('deleteStowageInstruction', 'Delete stowage instruction?')
            : t('deleteStowageInstructions', 'Delete stowage instructions?')
        }
        message={
          deleteItems.length === 1
            ? t(
                'deleteStowageInstructionMessage',
                'Stowage instruction {object} will be deleted.',
                { object: deleteItems[0].name },
              )
            : t(
                'deleteStowageInstructionsMessage',
                'Stowage instructions {object} will be deleted.',
                { object: deleteItems.map(item => item.name).join(', ') },
              )
        }
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='xs'
        onConfirm={() => handleDelete()}
        onClose={() => setDeleteItems([])}
      />
    </>
  )
})
